import * as React from 'react'

import { Layer, MapRef, Source, useMap } from 'react-map-gl/mapbox'

import {
  collisionPadding,
  hiddenOpacity,
  labelCasingPaint,
  labelHaloPaint,
  labelTypographyLayout,
  locationLabelInkPaint,
  locationPlateInkPaint,
  LOCATION_LABEL,
  SELECTED_TIER_PLACEMENT,
  SUPPRESSED_HALO_COLOR,
} from '~/client/src/shared/map/labelLayerStyle'
import {
  PLAIN_LABEL_PLATE,
  SELECTION_RING_MARGIN_PX,
} from '~/client/src/shared/map/labelPlateSpec'
import {
  getBoxedLabelLiftPx,
  getLabelLiftPx,
  getLabelPlatePadding,
} from '~/client/src/shared/map/locationLabelGeometry'
import {
  bandedImage,
  bandedImageBands,
  fittedImageSize,
  rampedOneLevelAhead,
} from '~/client/src/shared/map/mapFittedImage'
import {
  getZoomScale,
  zoomScaledSize,
} from '~/client/src/shared/map/mapZoomRule'
import {
  MARKER_SPEC,
  OFF_VIEW_BADGE_SIZE_PX,
  OFF_VIEW_OPACITY,
} from '~/client/src/shared/map/markerGeometry'

import {
  LOCATION_MARKERS_SELECTED_LAYER,
  locationMarkerLayerIds,
} from '../../MapBoxViewer/mapboxConstants'
import {
  MAX_ITEM_ZOOM_LEVEL,
  PROJECT_MARKER_ZOOM_LEVEL,
} from '../../MapBoxViewer/MapBoxViewer.store'
import {
  ICON_OCCLUSION_PAINT,
  TEXT_OCCLUSION_PAINT,
} from '../../MapBoxViewer/mapOcclusionRule'
import { useMapHoverProbe } from '../../MapBoxViewer/useMapHoverProbe'
import { useReleaseWithoutFade } from '../../MapBoxViewer/useReleaseWithoutFade'
import {
  labelPlateImageId,
  MARKER_SELECTION_RING_IMAGE_ID,
  OFF_VIEW_MARKER_SELECTION_RING_IMAGE_ID,
  offViewSelectionOutlineImageId,
  renderMissingMarkerImage,
  selectionOutlineImageId,
} from './GLMarkerImageGenerator'
import { stackedMarkerImage } from './GLStackChipImage'
import { useStackChipImages } from './useStackChipImages'
interface IProps {
  sourceId: string
  layerId: string
  data: GeoJSON.FeatureCollection<GeoJSON.Point>
  isZoomScalingEnabled?: boolean
  // The circle's size at full zoom.
  iconScale?: number
  isLabelOverlapEnabled?: boolean
  // The single item drawn with its highlighted image; null highlights none.
  highlightedItemId?: string
  minZoom?: number
  maxZoom?: number
  // Symbols aren't DOM nodes, so pointer intent arrives via hit-tests. The parent
  // resolves ids to items; this layer only reports what was pointed at.
  onHoverItem?: (itemId: string | null) => void
  onClickItem?: (itemId: string) => void
  // A stack — locations on one coordinate — lists its members rather than selecting the one drawn.
  onClickStack?: (stack: mapboxgl.GeoJSONFeature) => void
  onDoubleClickItem?: (itemId: string, part: LocationMarkerPart) => void
  // Paints the feature flagged isLifted from the selected tier, above the pins.
  liftsSelection?: boolean
  // The host's own click targets. A click that hits one of them is not a location click: the
  // lifted copy files no box under what it draws over, so a caption can sit inside it.
  yieldToLayerIds?: string[]
  // Every circle draws, whatever it overlaps: a host that places its own labels needs them all.
  isOverlapForced?: boolean
}

export type LocationMarkerPart = 'icon' | 'label'

// Which half of a marker a hit landed on. An item showing only one part is on a
// layer that answers directly; when both share a symbol, the split is the circle's
// bottom edge — the same line the label is offset to.
function hitPart(
  map: MapRef,
  hit: mapboxgl.GeoJSONFeature,
  point: mapboxgl.Point,
  isZoomScalingEnabled: boolean,
): LocationMarkerPart {
  if (hit.layer?.id?.includes('-label')) return 'label'
  if (hit.layer?.id?.includes('-icon')) return 'icon'

  const [longitude, latitude] = (hit.geometry as GeoJSON.Point).coordinates
  const anchor = map.project({ lng: longitude, lat: latitude })
  // The circle's own edge, deliberately — this splits a click between the icon half and the
  // label half, so the boundary is where the circle stops, not where the label's ink starts.
  // It rides the ramp because the circle does: at the bottom of the ramp a marker is a few
  // pixels tall, and a fixed 14px would call the whole label an icon.
  const radius = MARKER_SPEC.outerSize / 2
  const lift = isZoomScalingEnabled
    ? radius * getZoomScale(map.getZoom())
    : radius
  return point.y > anchor.y + lift ? 'label' : 'icon'
}

// The selection ring's quad is the whole outlined box, which reads as the object — so a press
// anywhere inside it grabs, rather than falling through to the map's pan handler. GRABBING
// only: hover and selection stay on the item's own symbols, or the empty part of the box would
// highlight and re-select it.
export const locationMarkerGrabLayerIds = (layerId: string): string[] => [
  ...locationMarkerLayerIds(layerId),
  `${layerId}-outline`,
  `${layerId}-outline-marker`,
]

// The eye-off badge is baked into the ring's image and GL hit-tests a symbol by its whole quad,
// so a point is on the badge when it lies within one badge of the quad's top and right edges.
export function hitsOffViewBadge(
  map: mapboxgl.Map,
  layerId: string,
  point: mapboxgl.Point,
  isZoomScalingEnabled: boolean,
): boolean {
  const outlines = [`${layerId}-outline`, `${layerId}-outline-marker`].filter(
    id => map.getLayer(id),
  )
  if (!outlines.length) return false
  const isOffViewAt = (x: number, y: number): boolean =>
    map
      .queryRenderedFeatures([x, y], { layers: outlines })
      .some(f => f.properties?.isOffView === true)
  if (!isOffViewAt(point.x, point.y)) return false
  const reach =
    OFF_VIEW_BADGE_SIZE_PX *
    (isZoomScalingEnabled ? getZoomScale(map.getZoom()) : 1)
  return (
    !isOffViewAt(point.x + reach, point.y) &&
    !isOffViewAt(point.x, point.y - reach)
  )
}

// The DOM label hangs off the circle's bottom edge, cleared by a fixed 27px
// spacer — a constant pixel distance, NOT a multiple of the label's font size.

// text-offset is LAYOUT, so placement accounts for the lift; text-translate is PAINT and moves
// pixels only, leaving the collision box on the point. Offsets are ems and must be literal, so
// the lift is divided by size across a step rather than with ['/'] — and it covers EVERY size
// the font field can produce, since a step floors to its nearest stop.
const LABEL_SIZE_MIN = 4
const LABEL_SIZE_MAX = 100
const LABEL_SIZE_STOPS = Array.from(
  { length: LABEL_SIZE_MAX - LABEL_SIZE_MIN + 1 },
  (_, i) => i + LABEL_SIZE_MIN,
)

// Where getLabelPlatePadding stops being flat and starts growing with the font. Padding is
// piecewise-linear in the label's size with this one joint, which is what lets the fit padding
// interpolate over three stops instead of stepping over ninety-seven.
const PLATE_PADDING_KNEE = 18

// The lift can vary with the label's size, so the px value is a function of it rather than a
// constant — the boxed label's padding grows with its font.
const liftOffsetEmOf = (pxFor: (size: number) => number): unknown => [
  'step',
  ['get', 'labelSize'],
  ['literal', [0, pxFor(LABEL_SIZE_STOPS[0]) / LABEL_SIZE_STOPS[0]]],
  ...LABEL_SIZE_STOPS.flatMap(size => [
    size,
    ['literal', [0, pxFor(size) / size]],
  ]),
]

// The plate's breathing room, in the [top, right, bottom, left] icon-text-fit-padding takes.
// Equal on all four sides, and no longer inset: the seam guard it used to be spent on was
// material the DOM box had no equivalent of, and the plate has neither a guard nor a border to
// hold outside its fill, so the padding is now the whole distance from text to painted edge.
const fitPaddingFor = (size: number): number[] => {
  const pad = getLabelPlatePadding(size)
  // Vertical is short by the radius, horizontal is not. The plate's corner rows now sit outside
  // the fitted box and are emitted at fixed size, so they already hold the text off the top and
  // bottom edges by the radius — asking for the full padding as well would double it and make
  // every location plate taller than it was. Left and right are unaffected: the content box
  // still spans the image horizontally, so nothing is added there.
  const padY = Math.max(0, pad - PLAIN_LABEL_PLATE.radiusPx)
  return [padY, pad, padY, pad]
}

// What the ring has to enclose: icon-text-fit measures the text's line box, which is right for a
// bare label and wrong for a plated one, whose padding grows with the font.
export interface IOutlinedItem {
  size: number
  boxed: boolean
  hasIcon: boolean
}

export const outlinePaddingFor = ({
  size,
  boxed,
  hasIcon,
}: IOutlinedItem): number[] => {
  const radius = MARKER_SPEC.outerSize / 2
  const plate = boxed ? getLabelPlatePadding(size) : 0
  const side = plate + SELECTION_RING_MARGIN_PX
  // Up to the top of the circle the label hangs under, or to the plate's own edge when there is
  // no circle above to clear.
  const above = hasIcon
    ? (boxed ? getBoxedLabelLiftPx(size) : getLabelLiftPx(size)) + radius
    : plate
  return [Math.max(above, plate) + SELECTION_RING_MARGIN_PX, side, side, side]
}

// Read off the outlined feature, never baked from it: a layout value that changes with the
// selection re-lays out the source mid-parse, and GL then lets that stale re-parse land over the
// selection's own setData — the new item stays undrawn until something re-lays it out again.
// Three stops are exact for the same reason as paddingStepFor: every case is linear in the size
// on either side of the plate's knee.
const outlinePaddingStepFor = (factor: number): unknown => {
  const overSize = (item: Omit<IOutlinedItem, 'size'>): unknown => [
    'interpolate',
    ['linear'],
    ['get', 'labelSize'],
    ...[LABEL_SIZE_MIN, PLATE_PADDING_KNEE, LABEL_SIZE_MAX].flatMap(size => [
      size,
      ['literal', outlinePaddingFor({ ...item, size }).map(px => px * factor)],
    ]),
  ]
  const hasIcon = ['==', ['get', 'showIcon'], true]
  const boxed = ['==', ['get', 'boxedLabel'], true]
  return [
    'case',
    ['all', hasIcon, boxed],
    overSize({ boxed: true, hasIcon: true }),
    hasIcon,
    overSize({ boxed: false, hasIcon: true }),
    boxed,
    overSize({ boxed: true, hasIcon: false }),
    overSize({ boxed: false, hasIcon: false }),
  ]
}

// Ramped one level ahead of the tile, for the same reason as fitPadding below.
export const outlineFitPadding = (isZoomScalingEnabled: boolean): unknown =>
  rampedOneLevelAhead(isZoomScalingEnabled, outlinePaddingStepFor)

const IS_OFF_VIEW = ['==', ['get', 'isOffView'], true]

// The ring image for the band this tile was laid out in, exactly as the plate is chosen.
const outlineImageForBand = (isZoomScalingEnabled: boolean): unknown =>
  bandedImage(isZoomScalingEnabled, band => [
    'case',
    IS_OFF_VIEW,
    offViewSelectionOutlineImageId(band),
    selectionOutlineImageId(band),
  ])

// Scaled on the same curve as the text, so the box grows and shrinks with its label instead of
// holding a constant pixel inset that swamps small text — at the ramp's floor an unscaled 3px
// inset is as wide as the label it surrounds.
//
// The zoom interpolation wraps a per-label-size step, not the other way round — see zoomRamped.
//
// Three stops, exact rather than approximate: the padding is flat to PLATE_PADDING_KNEE and
// linear above, so interpolating through the knee reproduces both halves, and `interpolate`
// clamps outside its domain exactly as the step's defaults did. The offset below cannot do
// this — dividing px BY size makes a hyperbola, which three stops can only approximate.
const paddingStepFor = (factor: number): unknown => {
  const at = (size: number): unknown => [
    'literal',
    fitPaddingFor(size).map(px => px * factor),
  ]
  return [
    'interpolate',
    ['linear'],
    ['get', 'labelSize'],
    LABEL_SIZE_MIN,
    at(LABEL_SIZE_MIN),
    PLATE_PADDING_KNEE,
    at(PLATE_PADDING_KNEE),
    LABEL_SIZE_MAX,
    at(LABEL_SIZE_MAX),
  ]
}

// Ramped ONE LEVEL AHEAD of the tile, which is the whole trick. This is a layout property, so it
// is evaluated once per tile at the tile's own zoom, and GL then scales the fitted quad by
// icon-size = scale(zoom) / scale(tile + 1). Ramping it at the tile's level therefore renders
// scale(tile) / scale(tile + 1) short — 14% at zoom 14. Ramping it a level ahead cancels the
// divisor exactly and the padding lands on scale(zoom), the same curve the glyphs are on.
const fitPadding = (isZoomScalingEnabled: boolean): unknown =>
  rampedOneLevelAhead(isZoomScalingEnabled, paddingStepFor)

// Divides out the zoom level mapbox shapes a tile's text at, so the fitted box tracks the size
// the glyphs actually render at. getBoxedLabelFitStops carries the why.
const boxFitIconSize = fittedImageSize

// A boxed label drops further than a plain one: past the circle, the gap, and its own padding, so
// it is the plate's outer edge that clears the circle. No zoom band here any more — with the
// padding ramped a level ahead and the radius baked pre-divided by that level, every part of the
// distance rides the label's own curve, so one em offset per size holds at every zoom.
const boxedLiftOffsetEm = (): unknown => liftOffsetEmOf(getBoxedLabelLiftPx)

// The plate image for the band this tile was laid out in: icon-image is layout, so the step is
// evaluated once per tile at the tile's own zoom — the band the radius was baked against.
const boxImageForBand = (isZoomScalingEnabled: boolean): unknown =>
  bandedImage(isZoomScalingEnabled, labelPlateImageId)

// The location tier's size is per item — the editor sets it between 4px and 100px — so its
// sizing reaches the shared functions as an expression rather than a constant. This argument is
// what replaced the old `text-size` override, and why no layer needs one now.
const LABEL_SCALING_SIZE = ['get', 'labelSize']

const LABEL_LIFT_OFFSET_EM = liftOffsetEmOf(getLabelLiftPx)
const BOXED_LIFT_OFFSET_EM = boxedLiftOffsetEm()

// Each of these is hundreds of array literals and a pure function of one flag, while this layer
// re-renders on every hover — so both variants are built once at module load.
const zoomExpressions = (isZoomScalingEnabled: boolean) => ({
  boxImage: boxImageForBand(isZoomScalingEnabled),
  boxIconSize: boxFitIconSize(isZoomScalingEnabled),
  boxFitPadding: fitPadding(isZoomScalingEnabled),
  outlineImage: outlineImageForBand(isZoomScalingEnabled),
  outlineFitPadding: outlineFitPadding(isZoomScalingEnabled),
})

const SCALED_EXPRESSIONS = zoomExpressions(true)
const FLAT_EXPRESSIONS = zoomExpressions(false)

// Compared against `true` rather than used bare: filter expressions must
// type-check as boolean, and ['get', …] is typed `value`. `all` tolerates it,
// `any` does not — a bare get there invalidates the filter and the layer
// silently draws nothing.
const HAS_ICON = ['==', ['get', 'showIcon'], true]
const NO_ICON = ['!=', ['get', 'showIcon'], true]
const HAS_LABEL = ['==', ['get', 'showLabel'], true]
const NO_LABEL = ['!=', ['get', 'showLabel'], true]
const BOXED = ['==', ['get', 'boxedLabel'], true]
const NOT_BOXED = ['!=', ['get', 'boxedLabel'], true]

// Split by which parts an item displays, so no symbol ever carries an empty icon-image or
// text-field — empty entries still place collision boxes. The circle of a boxed marker and
// the box itself answer the same predicate: they are the two halves of one pair.
const MARKER_FILTER = ['all', HAS_ICON, HAS_LABEL, NOT_BOXED]
const ICON_ONLY_FILTER = ['all', HAS_ICON, NO_LABEL]
const BOXED_PAIR_FILTER = ['all', HAS_ICON, HAS_LABEL, BOXED]
const LABEL_ONLY_FILTER = ['all', NO_ICON, HAS_LABEL, NOT_BOXED]
const LABEL_BOXED_CENTER_FILTER = ['all', NO_ICON, HAS_LABEL, BOXED]
const IS_OUTLINED = ['==', ['get', 'isOutlined'], true]
// A label fits its ring to the text; a bare marker cannot, since icon-text-fit has nothing to
// measure, so it wears a fixed ring on the marker's own scale instead.
const OUTLINE_FILTER = ['all', IS_OUTLINED, HAS_LABEL]
const MARKER_OUTLINE_FILTER = ['all', IS_OUTLINED, HAS_ICON, NO_LABEL]
const IS_LIFTED = ['==', ['get', 'isLifted'], true]
const LIFTED_OPACITY = hiddenOpacity(IS_LIFTED)
const OFF_VIEW_FADE = ['case', IS_OFF_VIEW, OFF_VIEW_OPACITY, 1]
const ICON_PAINT = { ...ICON_OCCLUSION_PAINT, 'icon-opacity': OFF_VIEW_FADE }
const liftedFilter = (filter: unknown[]): unknown[] => [
  'all',
  filter,
  IS_LIFTED,
]

const SORT_KEY = { 'symbol-sort-key': ['get', 'sortKey'] }

// Applies to the two halves of a boxed marker only: they are separate symbols a couple of
// scaled pixels apart, so neither can afford an inset that stays the same size as they shrink.
// Zero still declutters — the painted areas just touch instead of holding 2px apart.
const NO_COLLISION_INSET = { 'icon-padding': 0, 'text-padding': 0 }

export function GLLocationMarkerLayer({
  sourceId,
  layerId,
  data,
  isZoomScalingEnabled = false,
  iconScale = 1,
  isLabelOverlapEnabled = true,
  highlightedItemId = null,
  minZoom = PROJECT_MARKER_ZOOM_LEVEL,
  maxZoom = MAX_ITEM_ZOOM_LEVEL,
  onHoverItem,
  onClickItem,
  onClickStack,
  onDoubleClickItem,
  liftsSelection = false,
  yieldToLayerIds,
  isOverlapForced = false,
}: IProps): JSX.Element {
  const { current: map } = useMap()
  const expressions = isZoomScalingEnabled
    ? SCALED_EXPRESSIONS
    : FLAT_EXPRESSIONS
  // Read by the dblclick handler, whose effect is not rebound when the flag changes.
  const isZoomScalingRef = React.useRef(isZoomScalingEnabled)
  isZoomScalingRef.current = isZoomScalingEnabled
  const layerIds = React.useMemo(
    () => [
      ...locationMarkerLayerIds(layerId),
      ...(liftsSelection
        ? locationMarkerLayerIds(LOCATION_MARKERS_SELECTED_LAYER)
        : []),
    ],
    [layerId, liftsSelection],
  )
  const yieldToKey = yieldToLayerIds?.join('|')
  useReleaseWithoutFade(
    map,
    liftsSelection
      ? ((data.features.find(f => f.properties?.isLifted)?.properties
          ?.itemId as string) ?? '')
      : '',
  )
  const hasFeatures = !!data.features.length

  const onHoverItemRef = React.useRef(onHoverItem)
  onHoverItemRef.current = onHoverItem
  useMapHoverProbe(map, {
    enabled: hasFeatures && !!onHoverItem,
    layerIds,
    throttleMs: 80,
    onHit: hit =>
      onHoverItemRef.current?.((hit?.properties?.itemId as string) ?? null),
  })

  // A resize re-frames the camera without re-tiling, so buckets keep the size stops they were
  // laid out against: GL narrows a size property to the stops bracketing [tileZoom, tileZoom+1]
  // and CLAMPS outside them, freezing a boxed label's box at the band its tile was built in.
  // Widening BOX_FIT_STOP_OFFSET cannot cover it — past 0.5 a band resolves to its neighbour's
  // stops. Re-setting the same data is the only public lever that re-runs layout at the zoom
  // actually on screen.
  const dataRef = React.useRef(data)
  dataRef.current = data

  // Every band the plate and ring expressions can select, warmed up front rather than on the
  // miss. icon-image is LAYOUT: a symbol laid out before its image exists gets no icon quad and
  // is dropped whole, text included, and styleimagemissing fires at most once per id — so the
  // band that lost the race stays bare for the session. Easy to lose here, because a label only
  // turns plated once the ground read resolves. The event stays as the style-reload fallback.
  React.useEffect(() => {
    const gl = map?.getMap()
    if (!gl) {
      return
    }
    bandedImageBands(!!isZoomScalingEnabled).forEach(band => {
      renderMissingMarkerImage(gl, labelPlateImageId(band))
      renderMissingMarkerImage(gl, selectionOutlineImageId(band))
    })
    renderMissingMarkerImage(gl, MARKER_SELECTION_RING_IMAGE_ID)
  }, [map, isZoomScalingEnabled])

  // Only the editor ever hides a selection, so other maps never pay for these.
  const hasOffView = data.features.some(f => f.properties?.isOffView)
  React.useEffect(() => {
    const gl = map?.getMap()
    if (!gl || !hasOffView) {
      return
    }
    bandedImageBands(!!isZoomScalingEnabled).forEach(band =>
      renderMissingMarkerImage(gl, offViewSelectionOutlineImageId(band)),
    )
    renderMissingMarkerImage(gl, OFF_VIEW_MARKER_SELECTION_RING_IMAGE_ID)
  }, [map, isZoomScalingEnabled, hasOffView])

  // The highlighted marker is a DISTINCT image, and nothing asks for it until an item is first
  // hovered or selected — same one-shot trap as the bands above. Keyed on the distinct ids, not
  // on `data`, so a drag re-emitting the collection every frame does no repeated work.
  const markerImageIds = React.useMemo(() => {
    const ids = new Set<string>()
    data.features.forEach(f => {
      const { iconImage, iconImageHighlighted } = f.properties ?? {}
      if (iconImage) ids.add(iconImage as string)
      if (iconImageHighlighted) ids.add(iconImageHighlighted as string)
    })
    return [...ids].sort().join(' ')
  }, [data])

  React.useEffect(() => {
    const gl = map?.getMap()
    if (!gl || !markerImageIds) {
      return
    }
    // At the scale they are drawn, so an enlarged icon is rasterized sharp.
    markerImageIds
      .split(' ')
      .forEach(id => renderMissingMarkerImage(gl, id, iconScale))
  }, [map, markerImageIds, iconScale])

  useStackChipImages(map, data, iconScale)

  React.useEffect(() => {
    if (!map) {
      return undefined
    }

    let isPending = false
    const onResize = (): void => {
      isPending = true
    }
    // At idle, not on the resize itself: the surface reframes the camera after the resize, and
    // laying out against the old zoom is the whole bug.
    const onIdle = (): void => {
      if (!isPending) {
        return
      }
      isPending = false
      const source = map.getSource(sourceId)
      if (source && 'setData' in source) {
        source.setData(dataRef.current as never)
      }
    }

    map.on('resize', onResize)
    map.on('idle', onIdle)

    return () => {
      map.off('resize', onResize)
      map.off('idle', onIdle)
    }
  }, [map, sourceId])

  // Latest handlers without rebinding the map listeners every render.
  const onClickRef = React.useRef(onClickItem)
  onClickRef.current = onClickItem
  const onClickStackRef = React.useRef(onClickStack)
  onClickStackRef.current = onClickStack
  const onDoubleClickRef = React.useRef(onDoubleClickItem)
  onDoubleClickRef.current = onDoubleClickItem

  React.useEffect(() => {
    if (!map || !hasFeatures) return undefined

    const hitAt = (point: mapboxgl.Point): mapboxgl.GeoJSONFeature | null => {
      // A sibling handler earlier in mapbox's fire loop can unmount the map — it iterates a
      // snapshot of the listeners, so this one still runs, and getLayer dereferences a style
      // that is already gone. Not isStyleLoaded(): it stays false while tiles stream in.
      if (!map.getMap().style) return null
      const present = layerIds.filter(id => map.getLayer(id))
      if (!present.length) return null
      return map.queryRenderedFeatures(point, { layers: present })[0] ?? null
    }
    const onClick = (e: mapboxgl.MapMouseEvent): void => {
      const hit = hitAt(e.point)
      const itemId = hit?.properties?.itemId as string
      if (!itemId) return
      const yieldTo = yieldToLayerIds?.filter(id => map.getLayer(id))
      if (
        yieldTo?.length &&
        map.queryRenderedFeatures(e.point, { layers: yieldTo }).length
      ) {
        return
      }
      if (hit.properties?.stackMembers && onClickStackRef.current) {
        onClickStackRef.current(hit)
        return
      }
      onClickRef.current?.(itemId)
    }
    const onDoubleClick = (e: mapboxgl.MapMouseEvent): void => {
      const hit = hitAt(e.point)
      const itemId = hit?.properties?.itemId as string
      if (!itemId) return
      onDoubleClickRef.current?.(
        itemId,
        hitPart(map, hit, e.point, isZoomScalingRef.current),
      )
    }

    map.on('click', onClick)
    map.on('dblclick', onDoubleClick)
    return () => {
      map.off('click', onClick)
      map.off('dblclick', onDoubleClick)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, hasFeatures, layerIds, yieldToKey])

  // Built ONCE per (expressions, highlight, overlap), not per render. react-map-gl guards its
  // per-key deepEqual behind an identity check on the whole layout object, so handing it a
  // fresh-but-identical object made it walk every mapbox expression these eight layers carry,
  // on every pointer event of a drag. Nothing was ever set — the comparison alone was the cost.
  const styles = React.useMemo(() => {
    const LABEL_SCALING = {
      isZoomScalingEnabled,
      size: LABEL_SCALING_SIZE,
    }
    // Pixels, so it rides the ramp with everything else. Flat, it outgrows the gap it is meant
    // to protect once the label shrinks — see labelDeclutterLayout.
    const collisionInset = collisionPadding(isZoomScalingEnabled)
    const iconLayout = {
      // An open location stays highlighted, as a selected pin stays blue.
      'icon-image': stackedMarkerImage([
        'case',
        ['any', ['==', ['get', 'itemId'], highlightedItemId ?? ''], IS_LIFTED],
        ['get', 'iconImageHighlighted'],
        ['get', 'iconImage'],
      ]),
      'icon-size': zoomScaledSize(isZoomScalingEnabled, iconScale),
      'icon-anchor': 'center',
      // Circles declutter unless forced: "Allow labels to overlap" governs text, never the markers.
      'icon-allow-overlap': isOverlapForced,
      ...(isOverlapForced && { 'icon-ignore-placement': true }),
      'icon-padding': collisionInset,
    }

    const textLayout = {
      'text-field': ['get', 'name'],
      // The tier's voice — Inter SemiBold, +1% tracking, 1.15 leading — but NOT its size: a
      // location's size is set per item in the editor and then rides the zoom ramp, so it is
      // overridden right after. This drops the DOM label's `font-weight: bold`, which the GL
      // layer used to mirror; Bold under the same casing reads as less casing, because the
      // glyph dominates it.
      ...labelTypographyLayout(LOCATION_LABEL, LABEL_SCALING),
      'text-allow-overlap': isLabelOverlapEnabled,
      'text-padding': collisionInset,
      // Location names do not wrap: they are short, and a wrap column would fight the
      // per-item font size.
      'text-max-width': 1000,
    }

    // An UNPLATED label is bare text on whatever the map is showing, so it takes the tier's
    // light/dark pair from the ground under it — the same treatment the pin and markup labels
    // already get. It cannot take the location's colour: that colour is chosen to read against
    // the basemap on a coloured circle, not behind glyphs on satellite imagery, and the editor
    // offers no way to change it for a label anyway.
    const textPaint = {
      'text-color': locationLabelInkPaint(),
      // Tone-matched, not the DOM label's fixed white: a white casing under white ink is
      // nothing at all.
      'text-halo-color': labelHaloPaint(),
      // The tier's casing, in the tier's PROPORTIONS rather than its pixels. Two things follow
      // from the ratio the pin tier fixed: core = width - blur, so 1.5/0.8 leaves a 0.70 core
      // where the DOM label's flat 1/0 left 1.00, and holding that ratio is what keeps a 4px
      // label's counters open as well as a 100px one's.
      ...labelCasingPaint(LOCATION_LABEL, LABEL_SCALING),
      ...TEXT_OCCLUSION_PAINT,
      'text-opacity': OFF_VIEW_FADE,
    }

    // Plated BY CHOICE keeps the location's colour; plated by a plan sheet takes the constant —
    // see locationPlateInkPaint. No halo inside the plate, asked for as a suppressed casing
    // rather than a literal zero so this layer is not the one place the ramp does not reach.
    const boxedTextPaint = {
      ...textPaint,
      'text-color': locationPlateInkPaint(),
      ...labelCasingPaint(LOCATION_LABEL, { suppressed: true }),
      'text-halo-color': SUPPRESSED_HALO_COLOR,
    }

    // A boxed label owns the symbol's icon-image (the border box), so it can never ride with
    // the circle — those items put the circle on the icon-only layer and the box here.
    const boxedLabelLayout = {
      ...textLayout,
      ...SORT_KEY,

      'icon-image': expressions.boxImage,
      'icon-text-fit': 'both',
      'icon-size': expressions.boxIconSize,
      'icon-text-fit-padding': expressions.boxFitPadding,
      'icon-allow-overlap': isLabelOverlapEnabled,
      // No collision inset, here or on the circle this box hangs under. Every other inset in
      // the pair rides the zoom ramp; a flat one does not, so at low zoom two of them outgrow
      // the gap between circle and box and the circle, placing first, evicts the box that names
      // it. The box's collision area is already its full painted extent, which is all a label
      // has to reserve.
      ...NO_COLLISION_INSET,
    }

    return {
      markerLayout: {
        ...iconLayout,
        ...textLayout,
        ...SORT_KEY,
        // Top edge on the point, then offset down to the circle's bottom;
        // mapbox defaults to 'center', which sits half a line-height high.
        'text-anchor': 'top',
        'text-offset': LABEL_LIFT_OFFSET_EM,
        // Circle and label share this symbol, and by default a symbol is all-or-nothing:
        // a colliding label would take its circle down with it. Optional text drops the
        // caption alone, which is what the boxed pair already does by being two symbols.
        'text-optional': true,
      },
      markerPaint: { ...textPaint, ...ICON_PAINT },
      iconOnlyLayout: { ...iconLayout, ...SORT_KEY },
      iconBoxedLayout: { ...iconLayout, ...SORT_KEY, ...NO_COLLISION_INSET },
      labelOnlyLayout: { ...textLayout, ...SORT_KEY, 'text-anchor': 'center' },
      labelOnlyPaint: textPaint,
      labelBoxedLayout: {
        ...boxedLabelLayout,
        'text-anchor': 'top',
        'text-offset': BOXED_LIFT_OFFSET_EM,
      },
      labelBoxedCenterLayout: { ...boxedLabelLayout, 'text-anchor': 'center' },
      boxedPaint: { ...boxedTextPaint, ...ICON_PAINT },
      outlineLayout: {
        ...textLayout,
        // The text is here only to size the ring: icon-text-fit measures it, and nothing
        // paints it.
        'text-field': ['get', 'name'],
        'text-anchor': [
          'case',
          ['==', ['get', 'showIcon'], true],
          'top',
          'center',
        ],
        // The lift is icon clearance, so it is keyed on the icon — not on the box. Without an
        // icon the label is drawn centred on the point with no offset at all
        // (labelOnlyLayout, labelBoxedCenterLayout), and keying this on boxedLabel instead hung
        // a text box's ring a whole boxed lift below the text it was supposed to be around.
        'text-offset': [
          'case',
          ['!=', ['get', 'showIcon'], true],
          ['literal', [0, 0]],
          ['==', ['get', 'boxedLabel'], true],
          BOXED_LIFT_OFFSET_EM,
          LABEL_LIFT_OFFSET_EM,
        ],
        'icon-image': expressions.outlineImage,
        'icon-text-fit': 'both',
        'icon-size': expressions.boxIconSize,
        'icon-text-fit-padding': expressions.outlineFitPadding,
        // Selection chrome takes no part in placement. Safe here, and only here, because
        // the layer holds exactly one feature — the item being edited, which has to stay
        // visible anyway — so there is nothing for an orphaned ring to outline.
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      outlinePaint: { 'text-opacity': 0, ...ICON_OCCLUSION_PAINT },
      markerOutlineLayout: {
        ...iconLayout,
        'icon-image': [
          'case',
          IS_OFF_VIEW,
          OFF_VIEW_MARKER_SELECTION_RING_IMAGE_ID,
          MARKER_SELECTION_RING_IMAGE_ID,
        ],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    }
  }, [
    expressions,
    highlightedItemId,
    isLabelOverlapEnabled,
    isZoomScalingEnabled,
    iconScale,
    isOverlapForced,
  ])

  // The resting copies hide the lifted feature by paint, and the twins draw it from the tier.
  const lifted = React.useMemo(() => {
    if (!liftsSelection) return null
    const hideText = { 'text-opacity': LIFTED_OPACITY }
    const hideIcon = { 'icon-opacity': LIFTED_OPACITY }
    const parts = [
      [LABEL_ONLY_FILTER, styles.labelOnlyLayout, styles.labelOnlyPaint],
      [BOXED_PAIR_FILTER, styles.labelBoxedLayout, styles.boxedPaint],
      [
        LABEL_BOXED_CENTER_FILTER,
        styles.labelBoxedCenterLayout,
        styles.boxedPaint,
      ],
      [MARKER_FILTER, styles.markerLayout, styles.markerPaint],
      [ICON_ONLY_FILTER, styles.iconOnlyLayout, ICON_OCCLUSION_PAINT],
      [BOXED_PAIR_FILTER, styles.iconBoxedLayout, ICON_OCCLUSION_PAINT],
    ] as const
    return {
      markerPaint: { ...styles.markerPaint, ...hideText, ...hideIcon },
      iconPaint: { ...ICON_OCCLUSION_PAINT, ...hideIcon },
      labelOnlyPaint: { ...styles.labelOnlyPaint, ...hideText },
      boxedPaint: { ...styles.boxedPaint, ...hideText, ...hideIcon },
      twins: locationMarkerLayerIds(LOCATION_MARKERS_SELECTED_LAYER).map(
        (id, index) => {
          const [filter, layout, paint] = parts[index]
          return {
            id,
            filter: liftedFilter(filter),
            layout: { ...layout, ...SELECTED_TIER_PLACEMENT },
            paint,
          }
        },
      ),
    }
  }, [styles, liftsSelection])

  const zoomRange = { minzoom: minZoom, maxzoom: maxZoom }

  // Split by which parts an item displays, so no symbol ever carries an empty
  // icon-image or text-field — empty entries still place collision boxes.
  //
  // Names under circles, in locationMarkerLayerIds order: mapbox places the topmost layer first,
  // so every circle claims its space before any name can take it. The ring stays above the
  // circles; it ignores placement, so its place in the stack only decides what it paints over.
  return (
    <Source id={sourceId} type="geojson" data={data}>
      <Layer
        id={`${layerId}-label`}
        type="symbol"
        filter={LABEL_ONLY_FILTER as never}
        layout={styles.labelOnlyLayout as never}
        paint={(lifted?.labelOnlyPaint ?? styles.labelOnlyPaint) as never}
        {...zoomRange}
      />
      <Layer
        id={`${layerId}-label-boxed`}
        type="symbol"
        filter={BOXED_PAIR_FILTER as never}
        layout={styles.labelBoxedLayout as never}
        paint={(lifted?.boxedPaint ?? styles.boxedPaint) as never}
        {...zoomRange}
      />
      <Layer
        id={`${layerId}-label-boxed-center`}
        type="symbol"
        filter={LABEL_BOXED_CENTER_FILTER as never}
        layout={styles.labelBoxedCenterLayout as never}
        paint={(lifted?.boxedPaint ?? styles.boxedPaint) as never}
        {...zoomRange}
      />
      <Layer
        id={layerId}
        type="symbol"
        filter={MARKER_FILTER as never}
        layout={styles.markerLayout as never}
        paint={(lifted?.markerPaint ?? styles.markerPaint) as never}
        {...zoomRange}
      />
      {/* A circle with nothing under it: the whole marker is this one symbol, so it declutters
          on the shared inset like any other. */}
      <Layer
        id={`${layerId}-icon`}
        type="symbol"
        filter={ICON_ONLY_FILTER as never}
        layout={styles.iconOnlyLayout as never}
        paint={(lifted?.iconPaint ?? ICON_PAINT) as never}
        {...zoomRange}
      />
      {/* The circle of a boxed marker, whose label is the separate symbol below it. Split from
          the layer above only to drop the inset that would have the two of them fighting. */}
      <Layer
        id={`${layerId}-icon-boxed`}
        type="symbol"
        filter={BOXED_PAIR_FILTER as never}
        layout={styles.iconBoxedLayout as never}
        paint={(lifted?.iconPaint ?? ICON_PAINT) as never}
        {...zoomRange}
      />
      <Layer
        id={`${layerId}-outline`}
        type="symbol"
        filter={OUTLINE_FILTER as never}
        layout={styles.outlineLayout as never}
        paint={styles.outlinePaint as never}
        {...zoomRange}
      />
      <Layer
        id={`${layerId}-outline-marker`}
        type="symbol"
        filter={MARKER_OUTLINE_FILTER as never}
        layout={styles.markerOutlineLayout as never}
        paint={ICON_OCCLUSION_PAINT as never}
        {...zoomRange}
      />
      {lifted?.twins.map(twin => (
        <Layer
          key={twin.id}
          id={twin.id}
          type="symbol"
          filter={twin.filter as never}
          layout={twin.layout as never}
          paint={twin.paint as never}
          {...zoomRange}
        />
      ))}
    </Source>
  )
}
